import { ZuruflexApiError } from '@zuruflex/api-client';
import { PARTNER_KINDS, type PartnerKind } from '@zuruflex/types';
import {
  Alert,
  Button,
  FormField,
  GoogleButton,
  Input,
  PasswordInput,
  cn,
  type GoogleProfile,
} from '@zuruflex/ui';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { googleClientId } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const MIN_PASSWORD_LENGTH = 12;

/** A Google press that found no account, on its way to becoming one. */
export interface GooglePrefill {
  accessToken: string;
  profile: GoogleProfile;
}

interface SignUpFormProps {
  notice?: string | null;
  /**
   * The credential from a Google press that found no account. Its profile
   * fills the form so nobody is asked for their own name and address a
   * second time, and the token itself finishes the sign-up — which is why
   * it travels with the profile rather than being thrown away.
   */
  prefill?: GooglePrefill | null;
  googleEnabled: boolean;
  onBackToSignIn: () => void;
}

/**
 * One form for an individual with a Corolla and for a fleet of thirty.
 *
 * A company's registered particulars are not asked for here — they are
 * collected with the documents during onboarding, so nobody abandons
 * sign-up hunting for a certificate they do not have to hand.
 */
export function SignUpForm({ notice, prefill, googleEnabled, onBackToSignIn }: SignUpFormProps) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const { signUp, signInWithGoogle } = useAuth();

  const [kind, setKind] = useState<PartnerKind>('INDIVIDUAL');
  const [displayName, setDisplayName] = useState(prefill?.profile.name ?? '');
  const [fullName, setFullName] = useState(prefill?.profile.name ?? '');
  const [email, setEmail] = useState(prefill?.profile.email ?? '');
  const [contactPhone, setContactPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Google's token, held until the form is complete enough to spend it.
   *
   * Holding it is the whole point: with a verified Google identity the API
   * wants no password at all, so somebody who arrived that way must not be
   * made to invent one to get past a form.
   */
  const [credential, setCredential] = useState<string | null>(prefill?.accessToken ?? null);

  /**
   * Whether to show the password box to somebody Google has already
   * vouched for. A box with a red asterisk beside it reads as an
   * instruction however the hint under it is worded, so on that path it
   * starts folded away behind a link and the account is made without one.
   */
  const [wantsPassword, setWantsPassword] = useState(false);

  /**
   * Typed names win — this only fills a box that is still empty, so neither
   * a second profile nor a re-render can overwrite a correction.
   *
   * The address is the exception, and takes over whatever is there: the API
   * signs the account up under the address on the token regardless, so
   * leaving a typed one on screen would show a lie in a box that is by then
   * read-only.
   */
  function fillFrom({ name, email: address }: GoogleProfile) {
    if (name) {
      setDisplayName((current) => current || name);
      setFullName((current) => current || name);
    }
    if (address) setEmail(address);
  }

  // Mount already seeded from `prefill`; this is for one that arrives after,
  // which is what happens when Google is pressed on this form rather than
  // on the sign-in page.
  const applied = useRef(prefill);
  useEffect(() => {
    if (!prefill || prefill === applied.current) return;
    applied.current = prefill;
    setCredential(prefill.accessToken);
    fillFrom(prefill.profile);
  }, [prefill]);

  /** Everything but the credential, shared by both ways of signing up. */
  function details() {
    return { kind, displayName, fullName, email, contactPhone };
  }

  /**
   * The same details with Google's filling any gaps, so pressing its button
   * on a form nobody has touched still sends a name and an address rather
   * than blanks the API would reject.
   */
  function detailsWith(profile: GoogleProfile) {
    return {
      kind,
      displayName: displayName || profile.name || '',
      fullName: fullName || profile.name || '',
      email: profile.email || email,
      contactPhone,
    };
  }

  /**
   * A held Google token is a credential in its own right, so nobody has to
   * invent a password beside it — but one typed anyway is kept, since the
   * account is allowed both and it is the only way back in if Google is
   * ever unreachable. Without a token this is an ordinary sign-up.
   */
  async function finish() {
    const input = password ? { ...details(), password } : details();
    if (!credential) {
      await signUp(input);
      return;
    }
    try {
      await signInWithGoogle({ accessToken: credential }, input);
    } catch (cause) {
      // A token Google minted an hour ago has stopped being proof of
      // anything. Dropping it puts the password field back and leaves the
      // Google button below as the way to get a fresh one.
      if (cause instanceof ZuruflexApiError && cause.status === 401) setCredential(null);
      throw cause;
    }
  }

  async function attempt(action: () => Promise<void>) {
    setError(null);
    setHint(null);
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  // What is still missing outranks how they got here.
  const info = hint ?? notice;

  return (
    <>
      <h1 className="mt-8 text-xl font-semibold text-text">{t('signUp.title')}</h1>
      <p className="mt-1 text-sm text-text-muted">{t('signUp.subtitle')}</p>

      {info ? (
        <Alert variant="info" className="mt-6">
          {info}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="danger" className="mt-6">
          {error}
        </Alert>
      ) : null}

      <form
        className="mt-6 flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void attempt(finish);
        }}
      >
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-medium text-text">{t('signUp.kind')}</legend>
          <div className="grid grid-cols-2 gap-2">
            {PARTNER_KINDS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={kind === option}
                onClick={() => setKind(option)}
                className={cn(
                  'rounded-field border p-3 text-left transition-colors',
                  kind === option
                    ? 'border-surface-inverse bg-surface-inverse text-text-inverse'
                    : 'border-border-default hover:border-border-strong',
                )}
              >
                <span className="block text-sm font-semibold">
                  {t(option === 'INDIVIDUAL' ? 'signUp.individual' : 'signUp.company')}
                </span>
                <span
                  className={cn(
                    'mt-0.5 block text-xs',
                    kind === option ? 'text-text-inverse/70' : 'text-text-muted',
                  )}
                >
                  {t(option === 'INDIVIDUAL' ? 'signUp.individualHint' : 'signUp.companyHint')}
                </span>
              </button>
            ))}
          </div>
        </fieldset>

        <FormField label={t('signUp.displayName')} hint={t('signUp.displayNameHint')} required>
          {(field) => (
            <Input
              {...field}
              value={displayName}
              required
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('signUp.yourName')} required>
          {(field) => (
            <Input
              {...field}
              value={fullName}
              autoComplete="name"
              required
              onChange={(event) => setFullName(event.target.value)}
            />
          )}
        </FormField>

        {/* The API takes the address off the verified token and ignores
            whatever was typed here, so on the Google path the box is shown
            rather than offered: an editable field whose edits go nowhere is
            worse than one that plainly cannot be edited. */}
        <FormField
          label={t('common:field.email')}
          hint={credential ? t('signUp.emailFromGoogle') : undefined}
          required
        >
          {(field) => (
            <Input
              {...field}
              type="email"
              value={email}
              autoComplete="username"
              required
              readOnly={Boolean(credential)}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('common:field.phone')} required>
          {(field) => (
            <Input
              {...field}
              type="tel"
              value={contactPhone}
              autoComplete="tel"
              placeholder="024 123 4567"
              required
              onChange={(event) => setContactPhone(event.target.value)}
            />
          )}
        </FormField>

        {credential && !wantsPassword ? (
          <button
            type="button"
            className="self-start text-sm font-medium text-text-muted underline underline-offset-2"
            onClick={() => setWantsPassword(true)}
          >
            {t('signUp.addPassword')}
          </button>
        ) : (
          <FormField
            label={t('common:field.password')}
            hint={credential ? t('signUp.passwordOptional') : undefined}
            required={!credential}
          >
            {(field) => (
              <PasswordInput
                {...field}
                value={password}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required={!credential}
                onChange={(event) => setPassword(event.target.value)}
                showLabel={t('common:action.showPassword')}
                hideLabel={t('common:action.hidePassword')}
              />
            )}
          </FormField>
        )}

        <Button type="submit" disabled={busy} className="mt-1 w-full">
          {busy ? t('common:state.working') : t('signUp.submit')}
        </Button>
      </form>

      {googleEnabled ? (
        <>
          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-border-default" />
            <span className="text-xs uppercase tracking-wide text-text-muted">
              {t('common:word.or')}
            </span>
            <span className="h-px flex-1 bg-border-default" />
          </div>
          {/* Google supplies the name and a verified address; the rest of
              the form above still has to be filled in, so this posts both. */}
          <GoogleButton
            clientId={googleClientId}
            text="signup_with"
            locale={i18n.language}
            onCredential={(accessToken, profile) => {
              const merged = detailsWith(profile);
              // Show what we are about to send, whether or not it goes.
              fillFrom(profile);
              setCredential(accessToken);
              // A phone number is the one thing Google cannot supply, so
              // rather than spend the credential on a call the API will
              // reject, keep it and say what is still missing. The form can
              // then be finished with the button above.
              if (Object.values(merged).some((value) => !value)) {
                setHint(t('signUp.googleNeedsMore'));
                return;
              }
              setHint(null);
              void attempt(() => signInWithGoogle({ accessToken }, merged));
            }}
          />
        </>
      ) : null}

      <p className="mt-6 text-center text-xs text-text-muted">{t('signUp.terms')}</p>

      <p className="mt-4 text-center text-sm text-text-muted">
        {t('signUp.haveAccount')}{' '}
        <button
          type="button"
          className="font-semibold text-text underline underline-offset-2"
          onClick={onBackToSignIn}
        >
          {t('common:action.signIn')}
        </button>
      </p>
    </>
  );
}
